/**
 * Componente personalizado para select de tipografías
 * Reemplaza el select nativo con un dropdown que muestra cada opción con su tipografía
 * Compatible con todos los navegadores (Firefox, Safari, Chrome, etc.)
 */
export default class FontSelect {
  constructor(selectElement) {
    this.selectElement = selectElement
    if (!selectElement) return

    this.isOpen = false
    this.selectedIndex = selectElement.selectedIndex
    this.options = Array.from(selectElement.options)
    
    this.init()
  }

  init() {
    // Ocultar el select original pero mantenerlo funcional
    this.selectElement.classList.add('font-select-original')

    // Crear el contenedor del dropdown personalizado
    this.container = document.createElement('div')
    this.container.className = 'font-select-container'

    // Crear el botón que muestra la opción seleccionada
    this.button = document.createElement('button')
    this.button.type = 'button'
    this.button.className = 'font-select-button w-full count-chars input-text'
    this.button.setAttribute('aria-haspopup', 'listbox')
    this.button.setAttribute('aria-expanded', 'false')

    // Crear el span para el texto seleccionado
    this.selectedText = document.createElement('span')
    this.selectedText.className = 'font-select-selected'
    this.updateSelectedText()

    // Crear el ícono de flecha
    const arrow = document.createElement('span')
    arrow.innerHTML = '▼'
    arrow.className = 'font-select-arrow'
    this.arrow = arrow

    this.button.appendChild(this.selectedText)
    this.button.appendChild(arrow)

    // Crear el dropdown
    this.dropdown = document.createElement('div')
    this.dropdown.className = 'font-select-dropdown'
    this.dropdown.setAttribute('role', 'listbox')

    // Crear las opciones
    this.options.forEach((option, index) => {
      const optionElement = this.createOptionElement(option, index)
      this.dropdown.appendChild(optionElement)
    })

    // Ensamblar el componente
    this.container.appendChild(this.button)
    this.container.appendChild(this.dropdown)

    // Insertar después del select original
    this.selectElement.parentNode.insertBefore(this.container, this.selectElement.nextSibling)

    // Event listeners
    this.button.addEventListener('click', (e) => {
      e.preventDefault()
      e.stopPropagation()
      this.toggle()
    })

    // Cerrar al hacer click fuera
    document.addEventListener('click', (e) => {
      if (!this.container.contains(e.target)) {
        this.close()
      }
    })

    // Manejar teclado
    this.button.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault()
        this.toggle()
      } else if (e.key === 'ArrowDown') {
        e.preventDefault()
        this.open()
        this.highlightNext()
      } else if (e.key === 'Escape') {
        this.close()
      }
    })

    // Sincronizar cambios del select original (por si se cambia programáticamente)
    this.selectElement.addEventListener('change', () => {
      this.selectedIndex = this.selectElement.selectedIndex
      this.updateSelectedText()
      this.updateHighlight()
    })
  }

  createOptionElement(option, index) {
    const optionElement = document.createElement('div')
    optionElement.className = 'font-select-option'
    optionElement.style.fontFamily = `'${option.value}', sans-serif`
    optionElement.textContent = option.text
    optionElement.setAttribute('role', 'option')
    optionElement.setAttribute('data-index', index)
    optionElement.setAttribute('data-value', option.value)

    // Click handler
    optionElement.addEventListener('click', () => {
      this.selectOption(index)
    })

    return optionElement
  }

  updateSelectedText() {
    if (this.options[this.selectedIndex]) {
      const selectedOption = this.options[this.selectedIndex]
      this.selectedText.textContent = selectedOption.text
      this.selectedText.style.fontFamily = `'${selectedOption.value}', sans-serif`
    }
  }

  updateHighlight() {
    const optionElements = this.dropdown.querySelectorAll('.font-select-option')
    optionElements.forEach((el, index) => {
      el.classList.remove('highlighted')
      if (index === this.selectedIndex) {
        el.classList.add('selected')
      } else {
        el.classList.remove('selected')
      }
    })
  }

  selectOption(index) {
    this.selectedIndex = index
    this.selectElement.selectedIndex = index
    
    // Disparar evento change en el select original
    const changeEvent = new Event('change', { bubbles: true })
    this.selectElement.dispatchEvent(changeEvent)

    this.updateSelectedText()
    this.updateHighlight()
    this.close()
  }

  toggle() {
    if (this.isOpen) {
      this.close()
    } else {
      this.open()
    }
  }

  open() {
    this.isOpen = true
    this.dropdown.classList.add('open')
    this.button.setAttribute('aria-expanded', 'true')
    this.updateHighlight()

    // Scroll hasta la opción seleccionada
    const selectedOption = this.dropdown.querySelector(`[data-index="${this.selectedIndex}"]`)
    if (selectedOption) {
      selectedOption.scrollIntoView({ block: 'nearest' })
    }
  }

  close() {
    this.isOpen = false
    this.dropdown.classList.remove('open')
    this.button.setAttribute('aria-expanded', 'false')
  }

  highlightNext() {
    const optionElements = this.dropdown.querySelectorAll('.font-select-option')
    if (optionElements.length === 0) return

    let nextIndex = this.selectedIndex + 1
    if (nextIndex >= optionElements.length) {
      nextIndex = 0
    }

    const nextOption = optionElements[nextIndex]
    if (nextOption) {
      nextOption.scrollIntoView({ block: 'nearest' })
      // Resaltar visualmente usando clases
      optionElements.forEach((el, index) => {
        el.classList.remove('highlighted')
        if (index === nextIndex || index === this.selectedIndex) {
          el.classList.add('highlighted')
        }
      })
    }
  }

  // Método público para actualizar las opciones si cambian dinámicamente
  updateOptions() {
    this.options = Array.from(this.selectElement.options)
    this.selectedIndex = this.selectElement.selectedIndex
    
    // Limpiar y recrear el dropdown
    this.dropdown.innerHTML = ''
    this.options.forEach((option, index) => {
      const optionElement = this.createOptionElement(option, index)
      this.dropdown.appendChild(optionElement)
    })
    
    this.updateSelectedText()
    this.updateHighlight()
  }

  // Método público para destruir el componente
  destroy() {
    if (this.container && this.container.parentNode) {
      this.container.parentNode.removeChild(this.container)
    }
    if (this.selectElement) {
      this.selectElement.classList.remove('font-select-original')
    }
  }
}

